import { getPval } from "@/utils/statistics.js";
import { calcRange } from "@/plots/utils.js";
import { getString } from "@/utils/string.js";
import * as echarts from "echarts";

function getSelectedDataInfo(pureData, id, xAtr, yAtr) {
  let center = null;
  let maxDist = -Infinity;
  pureData.forEach((item) => {
    if (item.ID == id) {
      center = item;
    }
    maxDist = Math.max(maxDist, item["LD"]);
  });
  return { center, maxDist };
}

function getData(name, pureData, id, xAtr, yAtr, coefX, coefY) {
  let data = [];
  let info = getSelectedDataInfo(pureData, id, xAtr, yAtr);

  let centerX = info.center[xAtr];
  let centerY = info.center[yAtr];
  let maxDist = Math.abs(info.maxDist);

  pureData.forEach((item) => {
    data.push([
      item[xAtr] / coefX,
      item[yAtr] / coefY,
      item["LD"],
      item.ID,
      item.RSID,
      item.GENE,
      item.Consequence,
      item.IMPACT,
    ]);
  });

  let xRange = calcRange(pureData, xAtr);
  xRange = [xRange[0] / coefX, xRange[1] / coefX];

  let yRange = calcRange(pureData, yAtr);
  yRange = [yRange[0] / coefY, yRange[1] / coefY];
  centerX /= coefX;
  centerY /= coefY;

  yRange[1] += 1;

  if (name === "GlmGwas") {
    yRange[0] -= 1;
    xRange[0] -= 1;
    xRange[1] += 1;
  }

  return { data, xRange, yRange, maxDist, centerX, centerY, info: info.center };
}

function getStrcuturedData(name, pureData, id) {
  if (name == "Glmm") {
    return getData(name, pureData, id, "x1", "y1", 1000000, 1);
  } else if (name == "Gwas") {
    return getData(name, pureData, id, "x2", "y2", 1000000, 1);
  } else {
    return getData(name, pureData, id, "x3", "y3", 1, 1);
  }
}

export function buildGlmmPlot(
  plotContainer,
  pureData,
  id,
  plotClickHandler,
  selecedImpact,
  resetZoom
) {

  let chart = echarts.init(plotContainer);

  if (resetZoom) {
    chart.dispatchAction({
      type: "dataZoom",
      start: 0,
      end: 100,
    });
    return;
  }

  let structuredData = getStrcuturedData("Glmm", pureData, id);

  let option = getOption(
    id,
    structuredData.data,
    "Selection",
    "",
    0,
    structuredData.maxDist,
    `Chromosome ${id.split("_")[0]} (Mbp)`,
    "−log₁₀(Pₛ)",
    structuredData.xRange,
    structuredData.yRange,
    "Position",
    "−log₁₀(Pₛ)",
    "value",
    "number",
    structuredData.centerX,
    structuredData.centerY,
    false,
    structuredData.info,
    true,
    selecedImpact
  );

  chart.setOption(option);

  chart.on("click", function (item) {
    plotClickHandler(item);
  });

  chart.on("restore", () => {
    chart.dispatchAction({
      type: "takeGlobalCursor",
      key: "dataZoomSelect",
      dataZoomSelectActive: true,
    });
  });
  chart.dispatchAction({
    type: "takeGlobalCursor",
    key: "dataZoomSelect",
    dataZoomSelectActive: true,
  });
}

export function buildGwasPlot(
  plotContainer,
  pureData,
  id,
  plotClickHandler,
  selecedImpact,
  resetZoom
) {

  let chart = echarts.init(plotContainer);

  if (resetZoom) {
    chart.dispatchAction({
      type: "dataZoom",
      start: 0,
      end: 100,
    });
    return;
  }

  let structuredData = getStrcuturedData("Gwas", pureData, id);

  let option = getOption(
    id,
    structuredData.data,
    "GWAS",
    "",
    0,
    structuredData.maxDist,
    `Chromosome ${id.split("_")[0]} (Mbp)`,
    "−log₁₀(Pᵦ)",
    structuredData.xRange,
    structuredData.yRange,
    "Position",
    "−log₁₀(Pᵦ)",
    "value",
    "number",
    structuredData.centerX,
    structuredData.centerY,
    true,
    structuredData.info,
    true,
    selecedImpact
  );

  chart.setOption(option);

  chart.on("click", function (item) {
    plotClickHandler(item);
  });
  chart.on("restore", () => {
    chart.dispatchAction({
      type: "takeGlobalCursor",
      key: "dataZoomSelect",
      dataZoomSelectActive: true,
    });
  });
  chart.dispatchAction({
    type: "takeGlobalCursor",
    key: "dataZoomSelect",
    dataZoomSelectActive: true,
  });
}

export function buildGlmmGwasPlot(
  plotContainer,
  gwasData,
  id,
  plotClickHandler,
  selecedImpact,
  resetZoom
) {
  let chart = echarts.init(plotContainer);

  if (resetZoom) {
    chart.dispatchAction({
      type: "dataZoom",
      start: 0,
      end: 100,
    });
    return;
  }

  let structuredData = getStrcuturedData("GlmGwas", gwasData, id);

  let option = getOption(
    id,
    structuredData.data,
    "Selection vs. GWAS",
    "",
    0,
    structuredData.maxDist,
    "−log₁₀(Pᵦ)",
    "−log₁₀(Pₛ) × sign(s ⋅ β)",
    structuredData.xRange,
    structuredData.yRange,
    "−log₁₀(Pᵦ)",
    "−log₁₀(Pₛ) × sign(s ⋅ β)",
    "value",
    "number",
    structuredData.centerX,
    structuredData.centerY,
    false,
    structuredData.info,
    true,
    selecedImpact
  );
  chart.setOption(option);

  chart.on("click", function (item) {
    plotClickHandler(item);
  });

  chart.on("restore", () => {
    chart.dispatchAction({
      type: "takeGlobalCursor",
      key: "dataZoomSelect",
      dataZoomSelectActive: true,
    });
  });
  chart.dispatchAction({
    type: "takeGlobalCursor",
    key: "dataZoomSelect",
    dataZoomSelectActive: true,
  });
}

function getOption(
  id,
  data,
  name,
  subname,
  minDist,
  maxDist,
  xAxe,
  yAxe,
  xRange,
  yRange,
  xName,
  yName,
  xType,
  yType,
  centerX,
  centerY,
  makeColorBar,
  selectedInfo,
  useImpactFilter,
  selectedImpacts
) {
  console.log();
  const categorizedData = {
    ACTIVE: [],
    DEACTIVE: [],
  };

  data.forEach((item) => {
    const impact = item[7];
    if ((useImpactFilter && selectedImpacts.includes(impact)) || !impact) {
      categorizedData["ACTIVE"].push(item);
    } else if (useImpactFilter) {
      categorizedData["DEACTIVE"].push(item);
    } else {
      categorizedData["ACTIVE"].push(item);
    }
  });

  const series = [];

  series.push({
    name: "DEACTIVE",
    type: "scatter",
    symbolSize: 4,
    data: categorizedData["DEACTIVE"],
    itemStyle: {
      opacity: 0.6,
      color: "#ddd",
    },
    label: { precision: 2 },
  });
  series.push({
    name: "ACTIVE",
    type: "scatter",
    symbolSize: 5,
    data: categorizedData["ACTIVE"].sort((a, b) => a[2] - b[2]),
    itemStyle: {
      opacity: 0.85,
    },
    label: { precision: 2 },
  });

  series.push({
    type: "effectScatter",
    symbolSize: 7,
    rippleEffect: {
      number: 2,
      period: 8,
      scale: 4,
      brushType: "stroke",
    },
    data: [
      [
        centerX,
        centerY,
        maxDist,
        id,
        selectedInfo.RSID,
        selectedInfo.GENE,
        selectedInfo.Consequence,
        selectedInfo.IMPACT,
      ],
    ],
  });

  return {
    title: {
      text: name,
      textStyle: {
        fontFamily: "Ubuntu",
        fontSize: 13,
        color: "#000",
      },
      subtext: subname,
      left: "center",
      top: 40,
    },
    grid: {
      left: 60,
      top: 80,
      bottom: 50,
      borderWidth: 1,
    },
    toolbox: {
      right: 40,
      top: 35,
      itemSize: 20,
      feature: {
        dataZoom: {
          iconStyle: {
            opacity: 0,
          },
          icon: null,
          title: { zoom: "", back: "" },
          brushStyle: {
            color: "rgba(165, 28, 48, 0.25)",
          },
        },
        restore: {
          icon: null,
          title: "",
        },
        saveAsImage: {
          pixelRatio: 5,
          icon: "image://data:image/png;base64,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",
          title: "",
        },
      },
    },
    visualMap: {
      hoverLink: false,
      min: minDist,
      max: maxDist,
      realtime: false,
      show: makeColorBar,
      dimension: 2,
      orient: "horizontal",
      right: "center",
      top: 4,
      left: 17,
      type: "continuous",
      calculable: false,
      itemWidth: 19,
      itemHeight: 289,
      seriesIndex: [1, 2],
      inRange: {
        // color: ['#5e4fa2', '#486cb0', '#3389bd', '#50a9af', '#6bc4a5', '#91d3a4', '#b3e0a2', '#d3ed9c', '#ebf7a0', '#f9fcb5', '#fff7b2', '#fee695', '#fed07e', '#fdb567', '#fa9656', '#f57245', '#e75948', '#d63f4f', '#ba2049', '#9e0142'], //Spectral
        color: [
          "#000080",
          "#0000bb",
          "#0000f6",
          "#0020ff",
          "#0054ff",
          "#008cff",
          "#00c0ff",
          "#0ff8e7",
          "#39ffbe",
          "#66ff90",
          "#90ff66",
          "#beff39",
          "#e7ff0f",
          "#ffd300",
          "#ffa300",
          "#ff6f00",
          "#ff3f00",
          "#f60b00",
          "#bb0000",
          "#800000",
        ], //jet
        // color: ['#3b4cc0', '#4a63d3', '#5a78e4', '#6c8ff1', '#7ea1fa', '#92b4fe', '#a3c2fe', '#b6cefa', '#c6d6f1', '#d6dce4', '#e3d9d3', '#efcfbf', '#f5c2aa', '#f7b194', '#f59f80', '#ef886b', '#e57058', '#d75445', '#c73635', '#b40426'], //coolwarm
      },
    },
    tooltip: {
      trigger: "item",
      formatter: function (item) {
        let cx = item.data[0];
        let cy = item.data[1];
        let cd = item.data[2];
        let id = item.data[3];
        let rsId = getString(item.data[4]);
        let gene = getString(item.data[5]);
        let consequence = getString(item.data[6]);
        let impact = getString(item.data[7]);
        let info = `<b style="color: black; padding: 4px; border-radius: 2px; background-color: rgba(201, 209, 210, 0.3);">Click to explore this variant</b><br><br>`;
        return (
          info +
          `<b>Variant ID: </b> ${id}<br><b>RSID: </b>${rsId}<br><b>Gene: </b>${gene}<br><b>Impact: </b>${impact}<br><b>Consequence: </b>${consequence}<br><b>${xName}:</b> ${cx.toFixed(
            2
          )}<br><b>${yName}:</b> ${cy.toFixed(
            2
          )}<br><b>LD (r<sup>2</sup>): </b> ${cd.toFixed(2)}`
        );
      },
      textStyle: {
        fontFamily: "sans-serif",
        fontSize: 12,
        padding: [16, 16],
      },
    },
    xAxis: [
      {
        type: "value",
        name: xAxe,
        nameLocation: "center",
        splitNumber: 4,
        nameTextStyle: {
          fontFamily: "Ubuntu",
          fontSize: 14,
          padding: [13, 13],
        },
        offset: 3,
        min: xRange[0],
        max: xRange[1],
        splitLine: {
          show: false,
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: yAxe,
        scale: true,
        offset: 3,
        nameLocation: "center",
        nameTextStyle: {
          fontFamily: "Ubuntu",
          fontSize: 14,
          padding: [16, 16],
        },

        axisLabel: {
          formatter: function (value) {
            return Math.floor(value);
          },
        },

        min: yRange[0],
        max: yRange[1],
        splitLine: {
          show: false,
        },
      },
    ],
    legend: {
      type: "scroll",
      show: false,
      orient: "vertical",
      right: 10,
      top: 5,
      bottom: 20,
    },
    series: series,
  };
}
